import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { BACKEND_URL } from '../lib/api';
import api from '../lib/api';

function googleSignup() {
  window.location.href = `${BACKEND_URL}/api/auth/google`;
}

export default function Signup() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { acceptInvite } = useAuth();
  const nav = useNavigate();

  const [inv, setInv] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(!!token);
  const [tokenError, setTokenError] = useState('');

  const [form, setForm] = useState({ full_name: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenLoading(false);
      return;
    }

    setTokenLoading(true);
    setTokenError('');
    api.get(`/invitations/token/${token}`)
      .then((res) => {
        setInv(res.data.invitation);
      })
      .catch((err) => {
        setTokenError(err.response?.data?.error || 'Invalid or expired invitation token');
      })
      .finally(() => {
        setTokenLoading(false);
      });
  }, [token]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await acceptInvite({ token, ...form });
      nav('/dashboard');
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Failed to complete registration');
    } finally {
      setSubmitting(false);
    }
  };

  // Case 1: Token is present in URL
  if (token) {
    return (
      <div className="tm-board-bg flex items-center justify-center p-4 min-h-screen">
        <div className="tm-modal" style={{ maxWidth: 440, padding: 32 }} data-testid="signup-form">
          {tokenLoading && (
            <div className="text-center py-8">
              <div className="tm-loader" style={{ margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--trello-text-muted)', fontSize: 14 }}>Validating your invitation...</p>
            </div>
          )}

          {tokenError && (
            <div className="text-center">
              <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: '#DE350B', margin: '0 0 8px' }}>
                Invalid Invitation
              </h1>
              <p style={{ color: 'var(--trello-text-muted)', fontSize: 14, marginBottom: 20 }}>
                {tokenError}
              </p>
              <Link to="/login" className="tm-btn inline-block">
                Return to Login
              </Link>
            </div>
          )}

          {inv && !tokenLoading && (
            <>
              <div className="text-center mb-6">
                <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--trello-blue-dark)', margin: 0 }}>
                  Join {inv.company_name}
                </h1>
                <p style={{ color: 'var(--trello-text-muted)', marginTop: 8, fontSize: 14 }}>
                  {inv.invited_by} invited you as <b>{inv.role}</b>. Complete your registration below.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="tm-label">Email address</label>
                  <input
                    className="tm-input"
                    value={inv.email}
                    disabled
                    style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
                  />
                </div>

                <div>
                  <label className="tm-label">Full name</label>
                  <input
                    data-testid="signup-name"
                    className="tm-input"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="tm-label">Password</label>
                  <input
                    data-testid="signup-password"
                    className="tm-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Choose a password (min 6 characters)"
                    required
                    minLength={6}
                  />
                </div>

                {submitError && (
                  <div data-testid="signup-error" style={{ color: '#DE350B', fontSize: 13 }}>
                    {submitError}
                  </div>
                )}

                <button
                  data-testid="signup-submit"
                  type="submit"
                  className="tm-btn w-full"
                  disabled={submitting}
                  style={{ marginTop: 16 }}
                >
                  {submitting ? 'Creating account…' : 'Complete Registration'}
                </button>
              </form>

              <p className="text-center mt-6" style={{ fontSize: 14, color: 'var(--trello-text-muted)', margin: '20px 0 0' }}>
                Already registered? <Link data-testid="link-login" to="/login" style={{ color: 'var(--trello-blue)', fontWeight: 600 }}>Log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Case 2: No token parameter provided (Uninvited access to /signup)
  return (
    <div className="tm-board-bg flex items-center justify-center p-4 min-h-screen">
      <div className="tm-modal" style={{ maxWidth: 440, padding: 32 }} data-testid="signup-form">
        <div className="text-center mb-6">
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(0, 121, 191, 0.1)',
            color: '#0079BF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 22
          }}>
            ✉️
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--trello-blue-dark)', margin: '0 0 8px' }}>
            Registration by Invitation Only
          </h1>
          <p style={{ color: 'var(--trello-text-muted)', fontSize: 14, lineHeight: 1.5, margin: 0 }}>
            Users can register using the sign-up form only if they have received an invitation link from the system.
          </p>
        </div>

        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: 16,
          fontSize: 13,
          color: '#475569',
          lineHeight: 1.5,
          marginBottom: 24
        }}>
          If you have been invited to a workspace, please click the unique sign-up link provided in your invitation email to create your account.
        </div>

        <button
          data-testid="signup-google"
          onClick={googleSignup}
          className="w-full"
          style={{
            padding: '12px 14px',
            border: '1px solid var(--trello-border)',
            background: 'var(--trello-card-bg)',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontWeight: 600,
            fontSize: 14,
            transition: 'background 0.2s ease'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center mt-6" style={{ fontSize: 14, color: 'var(--trello-text-muted)', margin: '24px 0 0' }}>
          Already have an account? <Link data-testid="link-login" to="/login" style={{ color: 'var(--trello-blue)', fontWeight: 600 }}>Log in</Link>
        </p>
      </div>
    </div>
  );
}
